import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Upload, Loader2 } from 'lucide-react';
import L from 'leaflet';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? <Marker position={position} /> : null;
}

const CreatePost = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (useCurrentLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setPosition([lat, lng]);
          fetchAddress(lat, lng);
          toast.success('Current location detected');
        },
        (error) => {
          toast.error('Could not get current location');
          console.error(error);
        }
      );
    }
  }, [useCurrentLocation]);

  const fetchAddress = async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      setAddress(response.data.display_name);
    } catch (error) {
      console.error('Error fetching address:', error);
      setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    setImages(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!position) {
      toast.error('Please select a location on the map');
      return;
    }
    
    if (images.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('location', JSON.stringify({
        lat: position[0],
        lng: position[1],
        address: address
      }));
      
      images.forEach(image => {
        formData.append('images', image);
      });

      const token = localStorage.getItem('token');
      await axios.post(`${API}/posts`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Post created! Nearby rescuers have been notified.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                className="px-6 py-2.5 rounded-lg shadow-md cursor-pointer"
                onClick={() => navigate('/dashboard')}
              >
                <h1 className="text-xl font-bold text-white logo-text whitespace-nowrap">
                  STRAY SPOTTER
                </h1>
              </div>
            </div>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              data-testid="back-btn"
              className="hover:bg-teal-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-bold mb-8 text-gray-900" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Report a Stray Animal</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle>Animal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  data-testid="title-input"
                  placeholder="e.g., Injured dog near park"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  data-testid="description-input"
                  placeholder="Describe the animal's condition, appearance, behavior..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="images">Images * (Max 5)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="images"
                    data-testid="images-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    required
                  />
                  {images.length > 0 && (
                    <span className="text-sm text-gray-600">{images.length} file(s) selected</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Location *</span>
                <Button
                  type="button"
                  onClick={() => setUseCurrentLocation(true)}
                  data-testid="use-current-location-btn"
                  variant="outline"
                  size="sm"
                  style={{ color: 'rgb(26, 160, 157)', borderColor: 'rgb(26, 160, 157)' }}
                  className="hover:bg-teal-50"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Use Current Location
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="h-96 rounded-lg overflow-hidden" data-testid="location-map">
                <MapContainer
                  center={position || [51.505, -0.09]}
                  zoom={position ? 13 : 2}
                  className="h-full w-full"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <LocationMarker position={position} setPosition={setPosition} />
                </MapContainer>
              </div>
              
              {position && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(26, 160, 157, 0.1)' }}>
                  <p className="text-sm text-gray-700">
                    <strong>Selected Location:</strong> {address || 'Fetching address...'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Lat: {position[0].toFixed(6)}, Lng: {position[1].toFixed(6)}
                  </p>
                </div>
              )}
              
              <p className="text-sm text-gray-500">
                Click on the map to select the exact location where you spotted the animal
              </p>
            </CardContent>
          </Card>

          <Button
            type="submit"
            data-testid="submit-post-btn"
            style={{ backgroundColor: 'rgb(26, 160, 157)' }}
            className="w-full py-6 text-lg hover:opacity-90 text-white"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating Post & Notifying Rescuers...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Create Post & Notify Rescuers
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreatePost;
